import { Tabs } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { hasRole } from '@redux/authent/authent.selectors';
import { updateSelectedDrone } from '@redux/global/global.reducer';
import { selectSelectedDrone } from '@redux/global/global.selector';
import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { RoleEnum } from '@/types/authent/roles.types';
import { Drone, PartialDrone } from '@/types/utm/drone.types';
import { NormalizedFleet } from '@/types/utm/fleet.type';
import { Pilot } from '@/types/utm/pilot.types';

import CockpitPanelHeader from '../CockpitPanelHeader';
import Panel from '../Panel';
import DroneFormContainer from './drone/DroneFormContainer';
import Drones from './drone/Drones';
import FleetFormContainer from './fleet/FleetFormContainer';
import Fleets from './fleet/Fleets';
import PilotFormContainer from './pilot/PilotFormContainer';
import Pilots from './pilot/Pilots';

export enum UtmItemTypeEnum {
  DRONE = 'DRONE',
  PILOT = 'PILOT',
  FLEET = 'FLEET',
}

type ItemType =
  | {
      type: UtmItemTypeEnum.DRONE;
      value: PartialDrone | Drone | null;
    }
  | {
      type: UtmItemTypeEnum.PILOT;
      value: Partial<Pilot> | null;
    }
  | {
      type: UtmItemTypeEnum.FLEET;
      value: Partial<NormalizedFleet> | null;
    };

interface UtmContextType {
  editedItem: ItemType | null;
  setEditedItem: (value: ItemType | null) => void;
}

const UtmContext = createContext<UtmContextType | undefined>(undefined);

export const useUtmContext = (): UtmContextType => {
  const context = useContext<UtmContextType | undefined>(UtmContext);
  if (!context) {
    throw new Error('useUtmContext must be used within a UtmContext.Provider');
  }
  return context;
};

const editingComponent = {
  [UtmItemTypeEnum.DRONE]: DroneFormContainer,
  [UtmItemTypeEnum.PILOT]: PilotFormContainer,
  [UtmItemTypeEnum.FLEET]: FleetFormContainer,
};

export default function UtmManagement() {
  const { formatMessage } = useIntl();

  const canAccessDrones = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_DRONE));
  const canAccessPilots = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_PILOT));
  const canAccessFleets = useAppSelector((state) => hasRole(state, RoleEnum.ACCESS_FLEET));
  const selectedDrone = useAppSelector(selectSelectedDrone);
  const dispatchUpdateSelectedDrone = useWithDispatch(updateSelectedDrone);

  const [editedItem, setEditedItem] = useState<ItemType | null>(null);

  const getDefaultTab = () => {
    if (canAccessDrones) {
      return UtmItemTypeEnum.DRONE;
    } else if (canAccessPilots) {
      return UtmItemTypeEnum.PILOT;
    }
    return UtmItemTypeEnum.FLEET;
  };

  const [tabSelectedValue, setTabSelectedValue] = useState<UtmItemTypeEnum>(getDefaultTab());

  const _setEditedItem = useCallback((value: ItemType | null) => {
    value && setTabSelectedValue(value.type);
    setEditedItem(value);
  }, []);

  const contextValue = useMemo(() => ({ editedItem, setEditedItem: _setEditedItem }), [_setEditedItem, editedItem]);

  const isEditing = editedItem !== null;

  const EditingComponent = isEditing ? (editingComponent[editedItem.type] as FC) : null;

  useEffect(() => {
    if (selectedDrone === null) return;

    _setEditedItem({
      type: UtmItemTypeEnum.DRONE,
      value: selectedDrone,
    });

    dispatchUpdateSelectedDrone(null);
  }, [_setEditedItem, dispatchUpdateSelectedDrone, selectedDrone]);

  return (
    <UtmContext value={contextValue}>
      <Panel header={<CockpitPanelHeader label={formatMessage({ id: 'components.sidebar.UTM' })} />}>
        {EditingComponent ? (
          <EditingComponent />
        ) : (
          <Tabs.Root defaultValue={tabSelectedValue} size="md" lazyMount variant="light" userSelect="none">
            <Tabs.List>
              <Tabs.Trigger value={UtmItemTypeEnum.DRONE} disabled={!canAccessDrones}>
                <FormattedMessage id="components.utm.drones.title" />
              </Tabs.Trigger>
              <Tabs.Trigger value={UtmItemTypeEnum.PILOT} disabled={!canAccessPilots}>
                <FormattedMessage id="components.utm.pilots.title" />
              </Tabs.Trigger>
              <Tabs.Trigger value={UtmItemTypeEnum.FLEET} disabled={!canAccessFleets}>
                <FormattedMessage id="components.utm.fleets.title" />
              </Tabs.Trigger>
            </Tabs.List>
            <Tabs.ContentGroup>
              {canAccessDrones && (
                <Tabs.Content value={UtmItemTypeEnum.DRONE}>
                  <Drones />
                </Tabs.Content>
              )}
              {canAccessPilots && (
                <Tabs.Content value={UtmItemTypeEnum.PILOT}>
                  <Pilots />
                </Tabs.Content>
              )}
              {canAccessFleets && (
                <Tabs.Content value={UtmItemTypeEnum.FLEET}>
                  <Fleets />
                </Tabs.Content>
              )}
            </Tabs.ContentGroup>
          </Tabs.Root>
        )}
      </Panel>
    </UtmContext>
  );
}
